import { rootStore } from "../../redux/store";
import { setErrorState } from "../../redux/error_banner/actions";
import { createMargin, updateTreatmentPlan } from "../../helpers/backend_api";
import * as TreatmentPlan from "../../../src/helpers/tplan/tplan";
import { EMPTY_STRING, sceneOrientations } from "../../constants";
import { cemWarningCodes, cemWarningMsg } from "./constants";
import { clientErrorMessages, refFrames, SUCCESS } from "../../constants";
import AxialViewIcon from "../../ui/icons/AxialViewIcon";
import SagittalViewIcon from "../../ui/icons/SagittalViewIcon";
import CoronalViewIcon from "../../ui/icons/CoronalViewIcon";

export function formatPercent(percent) {
    return Math.min(percent * 100, 100).toFixed(1);
}

export function formatMarksScore(marksScore) {
    marksScore = marksScore === 1.0 ? 0.999 : marksScore;
    return formatPercent(marksScore);
}

export function formatVolume(volumeFloat) {
    return volumeFloat.toFixed(1);
}

export function formatLesionVolume(volumeFloat) {
    const volumeFmt = Math.max(volumeFloat, 0.1);
    let volumeStr = volumeFmt.toFixed(1);
    if (volumeFmt <= 0.1) {
        volumeStr = "<" + volumeStr;
    }

    return volumeStr;
}

export function formatProstateCoverage(percent) {
    let percentFmt = Math.min(percent * 100, 100);
    percentFmt = Math.max(percentFmt, 0.1);
    let percentStr = percentFmt.toFixed(1);
    if (percent <= 0.01) {
        percentStr = "<" + percentStr;
    }

    return percentStr;
}

export function isInsideDiv(e, divRect) {
    return (
        e.clientX >= divRect.left &&
        e.clientX <= divRect.right &&
        e.clientY >= divRect.top &&
        e.clientY <= divRect.bottom
    );
}

export function getCemWarningMsg(cemWarnings) {
    if (!cemWarnings || cemWarnings.length === 0) {
        return EMPTY_STRING;
    }

    let checkWarning = [];
    for (const code in cemWarningCodes) {
        checkWarning[cemWarningCodes[code]] = false;
    }
    for (let i = 0; i < cemWarnings.length; i++) {
        checkWarning[cemWarnings[i]] = true;
    }

    // Initialize the warning string with the base message
    let warningString = cemWarningMsg.WARNING_PREFIX;

    // Check conditions to append specific messages to the base warning
    if (checkWarning[cemWarningCodes.CANCER_MAP_UNDERSAMPLED_ROI_ONLY]) {
        warningString += cemWarningMsg.TARGETED;
    }

    if (checkWarning[cemWarningCodes.CANCER_MAP_UNDERSAMPLED_UNILATERAL]) {
        // Add "or" only if the previous condition was also true
        if (checkWarning[cemWarningCodes.CANCER_MAP_UNDERSAMPLED_ROI_ONLY]) {
            warningString += cemWarningMsg.CONNECTOR_OR;
        }
        warningString += cemWarningMsg.UNILATERAL;
    }

    if (checkWarning[cemWarningCodes.CANCER_MAP_UNDERSAMPLED_LARGE_PROSTATE]) {
        // Add "or" only if one of the previous conditions was true
        if (
            checkWarning[cemWarningCodes.CANCER_MAP_UNDERSAMPLED_ROI_ONLY] ||
            checkWarning[cemWarningCodes.CANCER_MAP_UNDERSAMPLED_UNILATERAL]
        ) {
            warningString += cemWarningMsg.CONNECTOR_OR;
        }
        warningString += cemWarningMsg.LARGE_PROSTATE;
    }

    warningString += cemWarningMsg.WARNING_POSTFIX;

    return warningString;
}

export function getNewLesionContour({
    currentTreatmentPlan,
    updatingMargin,
    setUpdatingMargin,
    userSelectedCpmThreshold,
    setUserSelectedProstateCoverage,
    setUserSelectedMarksScore,
    setUserSelectedLesionVolume,
    authToken,
    useruuid,
    patientuuid,
    planuuid,
    setCurrentTreatmentPlan,
    onMarginCreation,
}) {
    if (updatingMargin) {
        return;
    }

    setUpdatingMargin(true);

    createMargin(
        _getCreateMarginInput(
            userSelectedCpmThreshold,
            currentTreatmentPlan,
            authToken,
            planuuid
        )
    )
        .then((response) => {
            if (response && response.message === SUCCESS) {
                packPlanWithPayload({
                    currentTreatmentPlan,
                    payload: response.payload,
                    setUserSelectedProstateCoverage,
                    setUserSelectedMarksScore,
                    setUserSelectedLesionVolume,
                });
                updatePlan(
                    currentTreatmentPlan,
                    authToken,
                    useruuid,
                    patientuuid,
                    planuuid,
                    setCurrentTreatmentPlan
                );
            }
        })
        .catch(() => {
            rootStore.dispatch(
                setErrorState(true, clientErrorMessages.MARGIN_CREATION_FAILED)
            );
        })
        .finally(() => {
            setUpdatingMargin(false);

            if (onMarginCreation) {
                onMarginCreation();
            }
        });
}

function _getCreateMarginInput(
    userSelectedCPMThreshold,
    plan,
    authToken,
    planUUID
) {
    return {
        prostateURI: TreatmentPlan.getStructureInFrame(
            TreatmentPlan.getCoreStructure(plan.TreatmentPlan),
            refFrames.MRI
        ).URI,
        cpmURI: plan.SavedData.CPMURI,
        customCPMThreshold:
            userSelectedCPMThreshold != null
                ? userSelectedCPMThreshold
                : plan.SavedData.OptimalCPMThreshold,
        outputFormat: ".vtk",
        token: authToken,
        planUUID: planUUID,
    };
}

function packPlanWithPayload({
    currentTreatmentPlan,
    payload,
    setUserSelectedProstateCoverage,
    setUserSelectedMarksScore,
    setUserSelectedLesionVolume,
}) {
    currentTreatmentPlan.TreatmentPlan.StructureData.Margins[0].VolumeCC =
        payload.marginVolume;
    currentTreatmentPlan.TreatmentPlan.StructureData.CoreStructure.VolumeCC =
        payload.prostateVolume;
    currentTreatmentPlan.SavedData.UserSelectedCPMThreshold =
        payload.optimalCPMThreshold;
    currentTreatmentPlan.SavedData.UserSelectedMarksScore = payload.marksScore;
    currentTreatmentPlan.SavedData.UserSelectedLesionCoverageOfProstate =
        payload.prostateCoverage;
    currentTreatmentPlan.SavedData.IsPatientFTEligible =
        payload.focalTherapyEligible;

    setUserSelectedProstateCoverage(payload.prostateCoverage);
    setUserSelectedMarksScore(payload.marksScore);
    setUserSelectedLesionVolume(payload.marginVolume);

    return currentTreatmentPlan;
}

function updatePlan(
    plan,
    authToken,
    useruuid,
    patientuuid,
    planuuid,
    setCurrentTreatmentPlan
) {
    updateTreatmentPlan({
        useruuid: useruuid,
        patientuuid: patientuuid,
        planuuid: planuuid,
        token: authToken,
        plan: plan,
    })
        .then((payload) => payload.json())
        .then((json) => {
            setCurrentTreatmentPlan(json.payload.plan);
        })
        .catch(() => {
            rootStore.dispatch(
                setErrorState(true, clientErrorMessages.TPLAN_UPDATE_FAILED)
            );
        });
}

export function getMRIViewOrientation(orientation) {
    switch (orientation) {
        case sceneOrientations.AXIAL:
            return {
                ViewIcon: AxialViewIcon,
                label: "Axial View",
                showRepeatIcon: false,
            };
        case sceneOrientations.SAGITTAL:
            return {
                ViewIcon: SagittalViewIcon,
                label: "Sagittal View",
                showRepeatIcon: false,
            };
        case sceneOrientations.CORONAL:
            return {
                ViewIcon: CoronalViewIcon,
                label: "Coronal View",
                showRepeatIcon: false,
            };
        default:
            return {
                ViewIcon: CoronalViewIcon,
                label: "Endoscopic Coronal View", // surgery mode
                showRepeatIcon: true,
            };
    }
}
